import { useState } from "react";
import type { CommentInput, ReasonTag } from "@/app/reviewQueries";
import { Button } from "@/ui/Button";
import { RoleAvatar } from "@/routes/submission/ThreadView";
import { SNIPPETS } from "./model";

/** «+ комментарий» к ячейке: сниппеты (заменяют черновик), текст, причина,
 *  короткий пункт правок. Пустой текст не отправляется — причина рядом. */
export function Composer({
  cellId,
  taskN,
  draft,
  onDraft,
  reasons,
  me,
  onSubmit,
  onCancel,
}: {
  cellId: string;
  taskN?: number;
  draft: string;
  onDraft: (text: string) => void;
  reasons: ReasonTag[];
  me: string;
  onSubmit: (input: CommentInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [todo, setTodo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idBase = `c-${cellId}`;

  const submit = async () => {
    const body = draft.trim();
    if (!body) {
      setError("Пустой комментарий не отправится — напиши, что исправить.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const input: CommentInput = { cell_id: cellId, task_n: taskN, body };
      if (reason) input.reason = reason;
      if (todo.trim()) input.todo = todo.trim();
      await onSubmit(input);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Не удалось добавить комментарий. Текст остался — попробуй ещё раз.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-[26px_minmax(0,1fr)] gap-2.5 border-t border-bd bg-card px-3.5 py-3" data-testid="composer">
      <RoleAvatar name={me} student={false} />
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-1.5">
          {SNIPPETS.map((s) => (
            <button key={s.label} type="button" onClick={() => onDraft(s.text)} className="cursor-pointer rounded-[3px] border border-dashed border-bd2 bg-transparent px-2 py-[3px] font-mono text-[10.5px] hover:border-acc" style={{ color: "var(--ink2)" }}>
              {s.label}
            </button>
          ))}
        </div>
        <textarea
          value={draft}
          onChange={(e) => (onDraft(e.target.value), setError(null))}
          placeholder="комментарий к этой ячейке"
          rows={2}
          autoFocus
          className="w-full resize-y rounded-sm border border-bd2 bg-bg2 px-[11px] py-2 text-[13px] leading-[1.5] text-ink outline-none focus:border-acc"
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <label htmlFor={`${idBase}-reason`} className="flex flex-col gap-1">
            <span className="font-mono text-[11px] text-mut">причина</span>
            <select id={`${idBase}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm border border-bd2 bg-bg2 px-2 py-[7px] font-mono text-[12px] text-ink outline-none focus:border-acc">
              <option value="">без причины</option>
              {reasons.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={`${idBase}-todo`} className="flex flex-col gap-1">
            <span className="font-mono text-[11px] text-mut">пункт правок</span>
            <input id={`${idBase}-todo`} value={todo} onChange={(e) => setTodo(e.target.value)} placeholder="по умолчанию — первая строка" className="rounded-sm border border-bd2 bg-bg2 px-2 py-[7px] font-mono text-[12px] text-ink outline-none focus:border-acc" />
          </label>
        </div>
        {error && (
          <p role="alert" className="m-0 text-[12px] leading-[1.5]" style={{ color: "var(--err)" }}>
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <Button variant="primary" size="sm" className="py-[7px]" onClick={() => void submit()} disabled={busy} reason={busy ? "добавляем…" : undefined}>
            добавить
          </Button>
          <Button size="sm" className="py-[7px] text-ink2" onClick={onCancel}>
            отмена
          </Button>
        </div>
      </div>
    </div>
  );
}
